import {
  ChangeDetectionStrategy,
  Component,
  NgZone,
  OnDestroy,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  BehaviorSubject,
  combineLatest,
  filter,
  merge,
  Subject,
  takeUntil,
  tap,
} from 'rxjs';
import { DemoOrganisationType } from 'server/shared/models/demoOrganisationType';
import { AccountService } from 'src/app/services/account.service';
import { GoogleTagManagerService } from 'src/app/services/google-tag-manager.service';
import { queryParam$ } from 'src/helpers/queryParam$';
import { ToastService } from '../../services/toast/toast.service';
import { TranslationService } from '../../services/translation.service';
import { DemoSetupStep } from './demo-setup-step.enum';

@Component({
  selector: 'app-register-demo',
  templateUrl: './register-demo.component.html',
  styleUrls: ['./register-demo.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RegisterDemoComponent implements OnDestroy {
  i18n = this.translationService.getTranslations('register-demo');

  currentStep$ = new BehaviorSubject<DemoSetupStep>(
    DemoSetupStep.CONFIGURE_DEMO,
  );

  DemoSetupStep = DemoSetupStep;
  DemoOrganisationType = DemoOrganisationType;

  organisationType: DemoOrganisationType | null = null;
  organisationName = '';
  monthlyServices: number | null = null;

  showLoadingIndicator = false;
  currentLoadingStep$ = new BehaviorSubject<number>(0);
  loadingSteps = [
    this.i18n?.loadingSteps?.creatingOrganization,
    this.i18n?.loadingSteps?.generatingEmployees,
    this.i18n?.loadingSteps?.generatingOpenJobs,
    this.i18n?.loadingSteps?.generatingArchivedJobs,
  ];
  loadingProgress$ = new BehaviorSubject<number>(0);
  redirecting$ = new BehaviorSubject<boolean>(false);

  demoSetupCompleted$ = new Subject<string>();
  loadingStatesCompleted$ = new Subject<void>();
  private destroyed$ = new Subject<void>();

  // fast param is used in testing to skip artificial loading delay
  fastParamEnabled$ = queryParam$(this.route, 'fast').pipe(
    filter((fast) => fast === 'true'),
  );

  redirectToDemoEffect$ = combineLatest([
    this.demoSetupCompleted$,
    // either completed or fast enabled for artificial loading
    merge(this.loadingStatesCompleted$, this.fastParamEnabled$),
  ]).pipe(
    takeUntil(this.destroyed$),
    tap(([dispoEmail]) => {
      this.redirecting$.next(true);
      // Redirect to dashboard after 2 seconds
      setTimeout(() => {
        this.accountService.loginWithDemoInit(
          dispoEmail,
          (error: Meteor.Error) => {
            this.ngZone.run(() => {
              if (error) {
                this.toastService.add({
                  message: 'serverError',
                  type: 'ERROR',
                });
              } else {
                this.router.navigate(['/dashboard']).then();
              }
            });
          },
        );
      }, 300);
    }),
  );

  constructor(
    private toastService: ToastService,
    private translationService: TranslationService,
    private ngZone: NgZone,
    private router: Router,
    private route: ActivatedRoute,
    private accountService: AccountService,
    private googleTagManagerService: GoogleTagManagerService,
  ) {
    this.redirectToDemoEffect$.subscribe();
    this.googleTagManagerService.loadTagManager();
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
    if (this.animateTimeout) {
      clearTimeout(this.animateTimeout);
    }
  }

  async submitDemo(): Promise<void> {
    if (
      this.organisationType === null ||
      !this.organisationName ||
      !this.monthlyServices
    ) {
      this.toastService.add({
        message: this.i18n.fillAllFields || 'Bitte fülle alle Felder aus',
        type: 'ERROR',
      });
      return;
    }

    // Proceed with demo generation
    this.currentStep$.next(DemoSetupStep.GENERATING_DEMO);
    // Start the loading progress animation in parallel with demo setup
    this.startLoadingProgress();

    // Call setupDemo method with form data
    Meteor.call(
      'setupDemo',
      this.organisationName,
      this.organisationType,
      this.monthlyServices,
      (setupError: Meteor.Error, dispoEmail: string) => {
        this.ngZone.run(() => {
          if (setupError) {
            this.toastService.add({
              message: 'serverError',
              type: 'ERROR',
            });
            // cancel loading
            if (this.animateTimeout) {
              clearTimeout(this.animateTimeout);
              this.loadingProgress$.next(0);
              this.currentStep$.next(DemoSetupStep.CONFIGURE_DEMO);
            }
            // Go back to configuration step on error
            this.currentStep$.next(DemoSetupStep.CONFIGURE_DEMO);
            return;
          }
          // Emit that demo setup is completed
          this.demoSetupCompleted$.next(dispoEmail);
        });
      },
    );
  }

  startLoadingProgress(): void {
    this.currentLoadingStep$.next(1);
    // animate 0-25% with the empty setTimeout
    setTimeout(() => {
      this.loadingProgress$.next(25);
      this.animateProgress();
    });
  }

  animateTimeout: NodeJS.Timeout | null = null;

  animateProgress(): void {
    // Move to next step after random duration between 1 and 2 seconds
    const randomDelay = Math.random() * 300;
    const stepDuration = 1200 + randomDelay;
    this.animateTimeout = setTimeout(() => {
      this.ngZone.run(() => {
        // Immediately set progress to completed step (each step is 20% of total progress)
        this.loadingProgress$.next(
          ((this.currentLoadingStep$.value + 1) / this.loadingSteps.length) *
            100,
        );

        if (this.currentLoadingStep$.value >= this.loadingSteps.length) {
          // All steps completed
          this.loadingStatesCompleted$.next();
          return;
        }

        this.currentLoadingStep$.next(this.currentLoadingStep$.value + 1);
        this.animateProgress();
      });
    }, stepDuration);
  }
}
